import {
  Button,
  createColumnHelper,
  DEFAULT_TABLE_HEADER_ROW_HEIGHT,
  DEFAULT_TABLE_ROW_HEIGHT,
  EmptyState,
  Icon,
  Input,
  PaginationState,
  SortingState,
  Table,
  TableColumnDef,
  TablePaginationControls,
} from '@dittolive/anvil'
import { MagnifyingGlassIcon } from '@phosphor-icons/react'
import dayjs from 'dayjs'
import React, { useCallback, useMemo, useState } from 'react'
import AutoSizer from 'react-virtualized-auto-sizer'

import type { PortalApiKey } from '../../../types'
import ApiKeyActionMenu from './ApiKeyActionMenu'
import ApiKeyDeletionModal from './ApiKeyDeletionModal'
import ApiKeyInspector from './ApiKeyInspector'

type Props = {
  /**
   * List of API keys
   */
  apiKeys: PortalApiKey[]
  /**
   * Whether the viewer can create API keys
   */
  canCreate: boolean
  /**
   * Whether the viewer can delete API keys
   */
  canDelete: boolean
  /**
   * Whether the current user is a super admin
   */
  isSuperAdmin?: boolean
  /**
   * Callback when create button is clicked
   */
  onCreateClick?: () => void
  /**
   * Callback to delete an API key
   */
  onDelete: (apiKeyId: string) => Promise<void>
}

const columnHelper = createColumnHelper<PortalApiKey>()
const baseColumns = [
  columnHelper.accessor('description', {
    header: 'Description',
    cell: ({ getValue }) => (
      <span data-testid="apiKeyDescription" className="break-all">
        {getValue()}
      </span>
    ),
  }),
  columnHelper.accessor('expiresAt', {
    header: 'Expiry date',
    cell: ({ getValue }) => (
      <span data-testid="apiKeyExpiresAt" className="break-all">
        {dayjs(getValue()).format('LL')}
      </span>
    ),
    sortingFn: ({ original: a }, { original: b }) =>
      dayjs(a.expiresAt).diff(dayjs(b.expiresAt)),
  }),
]

// The max height of the table is 5 rows + the header row. Everything beyond that
// will be scrollable. This is mostly sylistic, so it doesn't grow too large.
const MAX_HEIGHT =
  DEFAULT_TABLE_HEADER_ROW_HEIGHT + DEFAULT_TABLE_ROW_HEIGHT * 5

export default function ApiKeyManager({
  apiKeys,
  canCreate,
  canDelete,
  isSuperAdmin = false,
  onCreateClick,
  onDelete,
}: Props) {
  const [filter, setFilter] = useState('')
  const [sorting, setSorting] = useState<SortingState>([])
  const [apiKeyForDeletion, setApiKeyForDeletion] = useState<PortalApiKey>()
  const [apiKeyForInspector, setApiKeyForInspector] = useState<PortalApiKey>()

  const columns = useMemo<TableColumnDef<PortalApiKey>[]>(
    () => [
      ...baseColumns,
      columnHelper.display({
        id: 'actions',
        cell: ({ row: { original: apiKey } }) => (
          <ApiKeyActionMenu
            viewerCanDelete={canDelete}
            onSelectForDeletion={() =>
              canDelete ? setApiKeyForDeletion(apiKey) : undefined
            }
            onSelectForInspection={() => setApiKeyForInspector(apiKey)}
          />
        ),
        size: 30,
        meta: {
          isActionColumn: true,
        },
        enableSorting: false,
      }),
    ],
    [canDelete],
  )

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 5,
  })

  const { pageIndex, pageSize } = pagination
  // NOTE: Client-side pagination should be fine for this use case, I don't foresee large datasets.
  // If this becomes a problem, we can revisit this by adding offset/limit to the query.
  const totalCount = apiKeys.length
  const filteredCount = useMemo(() => {
    const normalizedFilter = filter.trim().toLowerCase()

    if (!normalizedFilter) {
      return totalCount
    }

    return apiKeys.filter(({ description, expiresAt }) =>
      [description, expiresAt].some((value) =>
        value?.toLowerCase().includes(normalizedFilter),
      ),
    ).length
  }, [apiKeys, filter, totalCount])
  const pageCount = useMemo(
    () => Math.max(1, Math.ceil(filteredCount / pageSize)),
    [filteredCount, pageSize],
  )

  const showPagination = totalCount || (!totalCount && !!filter.length)

  const handleSetPage = useCallback(
    (page: number) => {
      setPagination((prev) => ({ ...prev, pageIndex: page - 1 }))
    },
    [setPagination],
  )

  const handleDeleted = () => {
    setApiKeyForDeletion(undefined)
  }

  return (
    <>
      {canDelete && (
        <ApiKeyDeletionModal
          apiKey={apiKeyForDeletion}
          onClose={() => setApiKeyForDeletion(undefined)}
          onDelete={async (apiKeyId) => {
            await onDelete(apiKeyId)
            handleDeleted()
          }}
        />
      )}

      <ApiKeyInspector
        apiKey={apiKeyForInspector}
        onClose={() => setApiKeyForInspector(undefined)}
        isSuperAdmin={isSuperAdmin}
      />

      <div className="w-full">
        <div className="flex h-12 items-center gap-2 px-2">
          <Input
            className="relative w-full"
            leadingIcon={
              <Icon
                className="text-foreground-subtle size-4"
                svg={<MagnifyingGlassIcon />}
              />
            }
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value)
              setPagination((prev) => ({ ...prev, pageIndex: 0 }))
            }}
            placeholder="Search API keys"
            disabled={apiKeys.length === 0 && !filter.length}
          />
          {canCreate && onCreateClick && (
            <Button variant="primary" onClick={onCreateClick}>
              New API key
            </Button>
          )}
        </div>

        <AutoSizer disableHeight>
          {({ width }: { width: number }) => (
            <Table
              columns={columns}
              data={apiKeys}
              width={width}
              height={(rowCount) =>
                rowCount > 0
                  ? Math.min(
                      MAX_HEIGHT,
                      DEFAULT_TABLE_HEADER_ROW_HEIGHT +
                        DEFAULT_TABLE_ROW_HEIGHT * rowCount,
                    )
                  : 156
              }
              activeOnHover={false}
              tableCellClassName="first:pl-4 first:sm:pl-6"
              tableHeaderCellClassName="first:pl-4 first:sm:pl-6"
              emptyStateRenderer={() => (
                <EmptyState
                  className="py-4"
                  message={
                    !!filter.length
                      ? 'No API keys match the current filter'
                      : 'No HTTP API keys yet'
                  }
                />
              )}
              options={{
                state: {
                  globalFilter: filter,
                  pagination,
                  sorting,
                },
                onPaginationChange: setPagination,
                onSortingChange: setSorting,
              }}
            />
          )}
        </AutoSizer>

        {showPagination && (
          <TablePaginationControls
            height={40}
            currentPage={pageIndex + 1}
            pageCount={pageCount}
            onSetPage={handleSetPage}
            centerContent={`${totalCount} API ${totalCount !== 1 ? 'keys' : 'key'}`}
          />
        )}
      </div>
    </>
  )
}
